import { useState, useEffect, useCallback } from 'react';
import { Anchor, ArrowRight, Loader2, Mail } from 'lucide-react';
import apiV2 from '../../utils/apiV2';
import { toast } from 'sonner';

const fmtPrice = (price) =>
  price ? (price.currency === 'AED' ? `AED ${price.amount.toLocaleString()}` : `$${price.amount.toLocaleString()}`) : '';

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '';
  }
};

/**
 * BookingFlows Pro — operator membership (Stripe Billing).
 * Failed payments only show a note; the software never locks.
 */
export const MembershipCard = ({ orgId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await apiV2.get(`/organizations/${orgId}/membership`);
      setData(r.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const m = params.get('membership');
    const sid = params.get('session_id');
    if (m === 'success' && sid) {
      window.history.replaceState({}, '', window.location.pathname);
      apiV2
        .post(`/organizations/${orgId}/membership/sync`, { session_id: sid })
        .then((r) => {
          setData(r.data);
          toast.success('Welcome to BookingFlows Pro — membership active');
        })
        .catch(() => toast.error('Payment received — status will update shortly'))
        .finally(() => setLoading(false));
      return;
    }
    if (m === 'portal_return') {
      window.history.replaceState({}, '', window.location.pathname);
      apiV2
        .post(`/organizations/${orgId}/membership/sync`, {})
        .then((r) => setData(r.data))
        .catch(() => load())
        .finally(() => setLoading(false));
      return;
    }
    if (m === 'cancelled') {
      window.history.replaceState({}, '', window.location.pathname);
      toast('Membership checkout cancelled');
    }
    load();
  }, [orgId, load]);

  const startCheckout = async (emailLink = false) => {
    setBusy(true);
    try {
      const r = await apiV2.post(`/organizations/${orgId}/membership/checkout`, {
        origin_url: window.location.origin,
        email_link: emailLink,
      });
      if (emailLink) {
        toast.success(r.data.emailed ? 'Checkout link sent to your inbox' : 'Link created — email could not be sent');
      } else {
        window.location.href = r.data.checkoutUrl;
      }
    } catch {
      toast.error('Could not start checkout');
    } finally {
      setBusy(false);
    }
  };

  const openPortal = async () => {
    setBusy(true);
    try {
      const r = await apiV2.post(`/organizations/${orgId}/membership/portal`, {
        origin_url: window.location.origin,
      });
      window.location.href = r.data.portalUrl;
    } catch {
      toast.error('Could not open the billing portal');
      setBusy(false);
    }
  };

  if (loading || !data) return null;

  const price = `${fmtPrice(data.price)}/mo`;

  return (
    <div className="card-soft p-6" data-testid="membership-card">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2.5">
          <span className="w-8 h-8 rounded-xl bg-blue-600/10 flex items-center justify-center">
            <Anchor className="w-4 h-4 text-blue-600" />
          </span>
          <h3 className="text-sm font-semibold text-gray-900">BookingFlows Pro</h3>
        </div>
        {data.isPro ? (
          <span
            className={`flex items-center gap-1.5 text-xs ${data.pastDue ? 'text-amber-600' : data.canceled ? 'text-gray-500' : 'text-gray-500'}`}
            data-testid="membership-status-pill"
          >
            <span className={`w-1.5 h-1.5 rounded-full ${data.pastDue ? 'bg-amber-500' : data.canceled ? 'bg-gray-400' : data.cancelAtPeriodEnd ? 'bg-amber-400' : 'bg-emerald-500'}`} />
            {data.pastDue ? 'Payment issue' : data.canceled ? 'Ended' : data.cancelAtPeriodEnd ? 'Canceling' : 'Active'}
          </span>
        ) : (
          <span className="text-xs font-semibold text-gray-900" data-testid="membership-price">{price}</span>
        )}
      </div>

      {!data.isPro && (
        <>
          <p className="text-xs text-gray-500 leading-relaxed mt-2 mb-4">
            For operators who take cash, bank transfer or their own card machine — one flat membership keeps every
            BookingFlows tool switched on, with no per-booking fees. Cancel anytime.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => startCheckout(false)}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold transition-colors disabled:opacity-60"
              data-testid="membership-join-btn"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
              Join for {price} <ArrowRight className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => startCheckout(true)}
              disabled={busy}
              className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-800 transition-colors disabled:opacity-60"
              data-testid="membership-email-link-btn"
            >
              <Mail className="w-3.5 h-3.5" /> Email me the checkout link
            </button>
          </div>
        </>
      )}

      {data.isPro && !data.canceled && data.cancelAtPeriodEnd && (
        <p className="text-xs text-gray-600 bg-gray-50 rounded-xl px-3.5 py-2.5 mt-3 leading-relaxed" data-testid="membership-cancel-note">
          Your membership is cancelled — your plan stays valid until{' '}
          <span className="font-semibold text-gray-900">{data.currentPeriodEnd ? fmtDate(data.currentPeriodEnd) : 'the end of your billing period'}</span>.
          You won't be charged again. Changed your mind? Renew anytime from Manage billing.
        </p>
      )}

      {data.isPro && data.pastDue && (
        <p className="text-xs text-amber-700 bg-amber-50 rounded-xl px-3.5 py-2.5 mt-3 mb-3 leading-relaxed" data-testid="membership-past-due-note">
          Your last payment didn't go through. Everything keeps working — please update your card when you get a
          moment.
        </p>
      )}

      {data.isPro && !data.canceled && (
        <div className="flex items-center justify-between mt-3">
          <span className="text-xs text-gray-400" data-testid="membership-period-text">
            {data.cancelAtPeriodEnd
              ? `${price} · valid until ${data.currentPeriodEnd ? fmtDate(data.currentPeriodEnd) : 'period end'}`
              : `${price}${data.currentPeriodEnd ? ` · renews ${fmtDate(data.currentPeriodEnd)}` : ''}`}
          </span>
          <button
            onClick={openPortal}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold transition-colors disabled:opacity-60"
            data-testid="membership-portal-btn"
          >
            {data.pastDue ? 'Update payment method' : 'Manage billing'}
          </button>
        </div>
      )}

      {data.isPro && data.canceled && (
        <div className="flex items-center justify-between mt-3">
          <span className="text-xs text-gray-400">Membership ended</span>
          <button
            onClick={() => startCheckout(false)}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold transition-colors disabled:opacity-60"
            data-testid="membership-rejoin-btn"
          >
            Rejoin for {price}
          </button>
        </div>
      )}
    </div>
  );
};

export default MembershipCard;
